import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/** Phase 1 — Promoter profile edit (Product Manager / Admin).
 *
 * No password reset here — admins can reset via /admin/users instead, keeping
 * the credential lifecycle consistent with the rest of the CRM.
 */
export default function PromoterEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [opts, setOpts] = useState({ dealers: [], provinces: [], product_managers: [], sales_managers: [], sales_heads: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/promoter/promoters/${id}`),
      api.get("/promoter/options"),
    ]).then(([p, o]) => { setProfile(p.data); setOpts(o.data); })
      .catch((e) => toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) }));
  }, [id]);

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: profile.name, phone: profile.phone, email: profile.email || null,
        date_of_birth: profile.date_of_birth || null, gender: profile.gender || null,
        address: profile.address || null,
        emergency_contact_name: profile.emergency_contact_name || null,
        emergency_contact_phone: profile.emergency_contact_phone || null,
        bank_name: profile.bank_name, bank_account_number: profile.bank_account_number,
        bank_account_holder_name: profile.bank_account_holder_name,
        promoter_type: profile.promoter_type,
        monthly_salary: profile.monthly_salary === "" ? null : profile.monthly_salary,
        incentive_eligible: !!profile.incentive_eligible,
        assigned_dealer_id: profile.assigned_dealer_id || null,
        assigned_dealer_name: profile.assigned_dealer_name || null,
        assigned_product_manager_id: profile.assigned_product_manager_id || null,
        assigned_sales_manager_id: profile.assigned_sales_manager_id || null,
        assigned_sales_head_id: profile.assigned_sales_head_id || null,
        province: profile.province || null, area: profile.area || null,
        joining_date: profile.joining_date || null,
        remarks: profile.remarks || null,
      };
      await api.put(`/promoter/promoters/${id}`, payload);
      toast.success("Promoter updated");
      navigate("/promoter/manage");
    } catch (err) {
      toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setSubmitting(false); }
  };

  if (!profile) return <div className="text-sm text-slate-500" data-testid="promoter-edit-loading">Loading…</div>;

  return (
    <form onSubmit={save} className="space-y-5" data-testid="promoter-edit-form">
      <header className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-3xl font-bold text-slate-900">{profile.name}</h1>
            <Badge className={profile.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>
              {profile.active ? "Active" : "Inactive"}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1 font-mono">{profile.promoter_id}</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/promoter/manage")}
                  data-testid="edit-cancel-btn">Back</Button>
          <Button type="submit" disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="edit-save-btn">{submitting ? "Saving..." : "Save changes"}</Button>
        </div>
      </header>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Name"><Input value={profile.name || ""} onChange={(e) => update("name", e.target.value)} /></F>
            <F label="Phone"><Input value={profile.phone || ""} onChange={(e) => update("phone", e.target.value)} /></F>
            <F label="Email"><Input value={profile.email || ""} onChange={(e) => update("email", e.target.value)} /></F>
            <F label="DOB"><Input type="date" value={profile.date_of_birth || ""} onChange={(e) => update("date_of_birth", e.target.value)} /></F>
            <F label="Gender">
              <Select value={profile.gender || ""} onValueChange={(v) => update("gender", v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{["Male", "Female", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Joining Date"><Input type="date" value={profile.joining_date || ""} onChange={(e) => update("joining_date", e.target.value)} /></F>
            <F label="Bank Name"><Input value={profile.bank_name || ""} onChange={(e) => update("bank_name", e.target.value)} /></F>
            <F label="Account Number"><Input value={profile.bank_account_number || ""} onChange={(e) => update("bank_account_number", e.target.value)} /></F>
            <F label="Holder Name"><Input value={profile.bank_account_holder_name || ""} onChange={(e) => update("bank_account_holder_name", e.target.value)} /></F>
            <F label="Promoter Type">
              <Select value={profile.promoter_type || "incentive_only"} onValueChange={(v) => update("promoter_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salaried">Salaried</SelectItem>
                  <SelectItem value="incentive_only">Incentive only</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Monthly Salary">
              <Input type="number" value={profile.monthly_salary ?? ""} onChange={(e) => update("monthly_salary", e.target.value === "" ? null : Number(e.target.value))} />
            </F>
            <F label="Incentive Eligible">
              <label className="flex items-center gap-2 h-9 text-sm text-slate-700">
                <Checkbox checked={!!profile.incentive_eligible} onCheckedChange={(v) => update("incentive_eligible", !!v)} />
                <span>Eligible</span>
              </label>
            </F>
            <F label="Dealer / Counter">
              <Select value={profile.assigned_dealer_id || ""} onValueChange={(v) => {
                const d = opts.dealers.find((x) => x.id === v);
                update("assigned_dealer_id", v);
                update("assigned_dealer_name", d?.name || "");
              }}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{opts.dealers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Product Manager">
              <Select value={profile.assigned_product_manager_id || ""} onValueChange={(v) => update("assigned_product_manager_id", v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{opts.product_managers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Sales Manager">
              <Select value={profile.assigned_sales_manager_id || ""} onValueChange={(v) => update("assigned_sales_manager_id", v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{opts.sales_managers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Sales Head">
              <Select value={profile.assigned_sales_head_id || ""} onValueChange={(v) => update("assigned_sales_head_id", v)}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{opts.sales_heads.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Province">
              <Select value={profile.province || ""} onValueChange={(v) => update("province", v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{opts.provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Area"><Input value={profile.area || ""} onChange={(e) => update("area", e.target.value)} /></F>
            <F label="Remarks"><Textarea rows={2} value={profile.remarks || ""} onChange={(e) => update("remarks", e.target.value)} /></F>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 text-xs text-slate-500">
          To reset this promoter&apos;s password, an Admin can use the Users page (/admin/users).
        </CardContent>
      </Card>
    </form>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
